import { useMemo } from 'react';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import { Badge } from '@savvy-kit/components/badge';
import { cn } from '@savvy-kit/lib/utils';
import { SidebarMenuButton, useSidebar } from '@savvy-kit/templates/sidebar';
import { isNil } from 'es-toolkit';
import { BellIcon } from 'lucide-react';
import { graphql } from 'src/gql';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';
import { assert } from '@savvy/utils/assert';

import { useActionCenterUrlState } from '../../action-center-inbox/action-center-url-state';
import { useNotificationFilterPreferencesValue } from '../../action-center-inbox/notification-filter-preferences';

export const notificationsMenuButtonQuery = graphql(/* GraphQL */ `
  query NotificationsMenuButton($userId: ID!, $alertTypes: [String!], $primaryAdvisorIds: [ID!], $groupBulk: Boolean) {
    alerts(
      userId: $userId
      alertTypes: $alertTypes
      primaryAdvisorIds: $primaryAdvisorIds
      first: 1
      groupBulk: $groupBulk
    ) {
      totalCount
    }
  }
`);

const ACTION_CENTER_PATH = '/advisor/dashboard/co-pilot';

function NotificationsMenuButton() {
  const { state } = useSidebar();
  const pathname = usePathname();

  const { user } = useAuth();
  assert(!isNil(user) && !isNil(user.id), 'User and ID should be defined');
  const userId: string = user.id;

  const urlState = useActionCenterUrlState();
  const persistedPrefs = useNotificationFilterPreferencesValue();

  const isActionCenterPage = pathname === ACTION_CENTER_PATH;
  const selectedAlertTypes = useMemo<readonly string[]>(
    () => (isActionCenterPage ? urlState.alertTypes : (persistedPrefs?.alertTypes ?? [])),
    [isActionCenterPage, urlState.alertTypes, persistedPrefs?.alertTypes],
  );
  const urlPrimaryAdvisorIds = useMemo<readonly string[] | null>(
    () => (isActionCenterPage ? urlState.primaryAdvisorIds : (persistedPrefs?.primaryAdvisorIds ?? null)),
    [isActionCenterPage, urlState.primaryAdvisorIds, persistedPrefs?.primaryAdvisorIds],
  );

  const serverPrimaryAdvisorIds = useMemo((): readonly string[] => {
    if (urlPrimaryAdvisorIds !== null) {
      return [...urlPrimaryAdvisorIds];
    }
    return [userId];
  }, [urlPrimaryAdvisorIds, userId]);

  const { data } = useQueryWithErrorLogging(notificationsMenuButtonQuery, {
    variables: {
      userId,
      alertTypes: selectedAlertTypes.length > 0 ? [...selectedAlertTypes] : undefined,
      primaryAdvisorIds: [...serverPrimaryAdvisorIds],
      // Count collapsed bulk cards, not their members, so the bell/side-nav badge matches the
      // grouped Inbox tab count (UNO-588).
      groupBulk: true,
    },
  });

  const notificationsCount = data?.alerts?.totalCount ?? 0;

  return (
    <SidebarMenuButton tooltip="Notifications">
      <NextLink href="/advisor/dashboard/co-pilot" className="relative flex items-center gap-2 w-full">
        <BellIcon className="size-4 shrink-0" />
        <Badge
          className={cn('absolute top-0 right-0', notificationsCount && state === 'collapsed' ? 'block' : 'hidden')}
          type="dot"
        />
        <span>Notifications</span>
        <Badge className={cn('ml-auto h-4.5 min-w-4.5', !notificationsCount && 'hidden')} type="circle">
          {notificationsCount}
        </Badge>
      </NextLink>
    </SidebarMenuButton>
  );
}

export default NotificationsMenuButton;
